import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { utils, models, styles, translations } from 'gipsy-misc'
import ReactTextareaAutocomplete from '@webscopeio/react-textarea-autocomplete'
import { createTaskRequest } from 'features/task/store/actions'
import { getCurrentPathName } from 'utils/location'
import { tags as tagsApi, contact as contactApi } from 'gipsy-api'
import { Tag as TagBadge } from 'features/list/components/line/components/badges'
import { Tag, UserSuggestion, TimeInput, Icon, media, UserPicture, Tooltip } from 'gipsy-ui'

const selectedBackgroundColor = 'rgb(222, 235, 255)'

const timeEstimateRegExp = new RegExp('^\\d+(h($|\\d{2}$)|min$)')

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      createTaskRequest
    },
    dispatch
  )
})

class InlineAddTask extends PureComponent {
  static propTypes = {
    group: PropTypes.string,
    assignee: PropTypes.object,
    withTags: PropTypes.bool
  }

  state = {
    focus: false,
    allTags: [],
    value: '',
    selectedTags: [],
    defaultContactSuggestions: [],
    timeout: null
  }

  fetchTags = async () => {
    try {
      const allTags = await tagsApi.get()
      this.setState({ allTags })
    } catch (err) {
      console.error(err)
    }
  }

  blur = () => {
    if (!this.state.focus) {
      return
    }
    this.setState({ focus: false }, () => this.textarea.blur())
  }

  focus = () => {
    if (this.state.focus) {
      return
    }
    this.setState({ focus: true }, () => this.textarea.focus())
  }

  onFocus = () => {
    this.setState({
      focus: true
    })

    if (!this.state.allTags || this.state.allTags.length === 0) {
      this.fetchTags()
    }

    if (!this.state.defaultContactSuggestions || this.state.defaultContactSuggestions.length === 0) {
      contactApi.get().then(contacts => {
        const suggestions = (contacts || []).map(contact => {
          contact.key = contact.userId || contact.email
          return contact
        })
        this.setState({ defaultContactSuggestions: suggestions })
      })
    }
  }

  onBlur = e => {
    if (e.type === 'click') {
      return
    }
    if (this.textarea.value !== '') {
      this.onSubmit()
    }
    this.setState({
      focus: false
    })
  }

  onKeyDown = e => {
    if (e.keyCode === 13) {
      this.blur()
      this.onSubmit()
    } else if (e.keyCode === 27) {
      this.cleanState()
      this.blur()
    } else if (e.keyCode === 32) {
      const words = this.state.value.split(' ')
      const lastWord = words[words.length - 1]
      if (lastWord.match(timeEstimateRegExp)) {
        this.parseTimeLastWord()
      }
    }
  }

  cleanState = () => {
    this.setState({ value: '', selectedTags: [], selectedAssignee: undefined, estimatedTime: undefined })
  }

  parseTimeLastWord = () => {
    const words = this.state.value.split(' ')
    const lastWord = words[words.length - 1]

    const lastIndex = this.state.value.lastIndexOf(' ')
    const valueWithoutTime = this.state.value.substring(0, lastIndex)

    if (lastWord.includes('min')) {
      const [minute] = lastWord.split('min')
      this.setState({
        estimatedTime: utils.datetime.getNanosecondsFromHourAndMinute({ hour: 0, minute: parseInt(minute, 10) }),
        value: valueWithoutTime
      })
    } else if (lastWord.includes('h')) {
      const [hour, minute] = lastWord.split('h')
      this.setState({
        estimatedTime: utils.datetime.getNanosecondsFromHourAndMinute({
          hour: parseInt(hour, 10),
          minute: parseInt(minute ? minute : '0', 10)
        }),
        value: valueWithoutTime
      })
    }
  }

  onSubmit = async () => {
    const { assignee } = this.props
    if (!this.textarea.value) {
      this.focus()
      return
    }

    const date = utils.datetime.getDateForGroup(this.props.group)

    const params = {
      title: this.textarea.value,
      when: { date: date }
    }
    if (assignee) {
      params.to = assignee
    } else if (this.state.selectedAssignee) {
      params.to = this.state.selectedAssignee
    }
    if (this.state.selectedTags) {
      params.tags = this.state.selectedTags
    }
    if (this.state.estimatedTime) {
      params.estimatedTime = this.state.estimatedTime
    }

    const task = Object.assign({}, models.task(params))
    const filledTask = utils.task.computeTaskBeforeCreate(task)

    const lastValue = this.textarea.value
    this.cleanState()

    const response = await this.props.actions.createTaskRequest(filledTask, this.props.history, {
      componentSource: 'inlineAddTask',
      pageSource: getCurrentPathName()
    })
    if (response && response.error) {
      this.textarea.value = lastValue
    }
  }

  onChange = e => {
    this.setState({ value: e.target.value })
  }

  handleItemSelected = ({ currentTrigger, item }) => {
    if (currentTrigger === '#') {
      let currentTags = this.state.selectedTags
      item.label = utils.badges.getTagLabel(item)
      currentTags.push(item)
      this.setState({ selectedTags: currentTags })
    }

    if (currentTrigger === '@') {
      this.setState({ selectedAssignee: item })
    }
  }

  handleTimeInputChange = ({ paramName, value }) => {
    const nanos = utils.datetime.getNanosecondsFromHourAndMinute(value)
    this.setState({ estimatedTime: nanos })
  }

  tagDataProvider = token => {
    return this.state.allTags
      ? this.state.allTags
          .map(tag => {
            tag.key = tag.id
            return tag
          })
          .filter(
            tag =>
              tag.name.toLowerCase().includes(token.toLowerCase()) &&
              utils.array.getObjectIndexInArray(this.state.selectedTags, tag, ['id']) === -1
          )
      : []
  }

  assigneeDataProvider = token => {
    if (this.state.timeout) {
      clearTimeout(this.state.timeout)
    }

    if (token === '') {
      return this.state.defaultContactSuggestions
    }

    return new Promise((resolve, reject) => {
      this.setState({
        timeout: setTimeout(() => {
          return contactApi.get(token).then(data => resolve(data || []))
        }, 400)
      })
    }).then(contacts =>
      contacts.map(contact => {
        contact.key = contact.userId || contact.email
        return contact
      })
    )
  }

  makeTrigger = () => {
    const { withTags, withAssignee } = this.props

    let trigger = {}
    if (withTags) {
      trigger['#'] = {
        dataProvider: this.tagDataProvider,
        component: TagItem,
        output: () => ''
      }
    }
    if (withAssignee) {
      trigger['@'] = {
        dataProvider: this.assigneeDataProvider,
        component: ContactItem,
        output: () => ''
      }
    }
    return trigger
  }

  makePlaceholder = () => {
    const { group, withTags, withAssignee } = this.props
    let placeholder = translations.inlineAddTask.addTask.replace('{{group}}', group)
    if (withTags) {
      placeholder = placeholder.concat(translations.inlineAddTask.tags)
    }
    if (withAssignee) {
      placeholder = placeholder.concat(translations.inlineAddTask.assignee)
    }
    placeholder = placeholder.concat(translations.inlineAddTask.estimatedTime)
    return placeholder
  }

  render() {
    const { focus, selectedTags, selectedAssignee, estimatedTime } = this.state
    const { hide } = this.props
    const { hour, minute } = utils.datetime.getHourAndMinuteFromNanoseconds(estimatedTime)

    return (
      <Container hide={hide} focus={focus} className="inline-add-task">
        <ButtonContainer>
          <PlusButton className="plus-button" onClick={this.onSubmit}>
            <Icon icon="Plus" size={11} />
          </PlusButton>
        </ButtonContainer>
        <TextareaContainer
          onItemSelected={this.handleItemSelected}
          className="textarea"
          loadingComponent={Loading}
          placeholder={this.makePlaceholder()}
          ref={rta => {
            this.rta = rta
          }}
          innerRef={textarea => {
            this.textarea = textarea
          }}
          value={this.state.value}
          onChange={this.onChange}
          minChar={0}
          onKeyDown={this.onKeyDown}
          onSubmit={this.onSubmit}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          trigger={this.makeTrigger()}
          containerStyle={{
            width: '100%',
            position: 'relative'
          }}
          dropdownStyle={{
            position: 'absolute',
            overflow: 'scroll',
            maxHeight: '200px',
            backgroundColor: 'white',
            borderRadius: '4px',
            border: `solid 1px ${styles.colors.lightGrey}`
          }}
          listStyle={{
            listStyleType: 'none',
            paddingLeft: '0px',
            marginBottom: '0px',
            marginTop: '0px'
          }}
          loaderStyle={{
            position: 'absolute',
            overflow: 'scroll',
            maxHeight: '200px',
            backgroundColor: 'white',
            borderRadius: '4px',
            border: `solid 1px ${styles.colors.lightGrey}`
          }}
        />
        {selectedAssignee && (
          <UserPicture src={selectedAssignee.picture} size={24} style={{ marginRight: '5px' }}>
            <Tooltip
              text={selectedAssignee.displayName || selectedAssignee.email}
              vs={35}
              hs={12}
              position="top left"
            />
          </UserPicture>
        )}
        {selectedTags && selectedTags.map(tag => <TagBadge key={tag.id} {...tag}></TagBadge>)}
        {estimatedTime && (
          <TimeInput
            showIncrementButtons={true}
            showPeriod={false}
            paramName="estimatedTime"
            hour={hour}
            minute={minute}
            onChange={this.handleTimeInputChange}
          />
        )}
      </Container>
    )
  }
}

export default withRouter(
  connect(
    null,
    mapDispatchToProps,
    null,
    {
      pure: true
    }
  )(InlineAddTask)
)

const TagItem = ({ selected, entity: tag }) => {
  return (
    <TagContainer key={tag.id} selected={selected}>
      <Tag id={tag.id} name={tag.name} color={tag.color} backgroundColor={tag.backgroundColor} mode="preview"></Tag>
    </TagContainer>
  )
}

const ContactItem = ({ selected, entity: contact }) => {
  return (
    <UserSuggestion
      preSelected={selected}
      preSelectedBackgroundColor={selectedBackgroundColor}
      user={contact}></UserSuggestion>
  )
}

const Loading = ({ data }) => {
  if (!data || data.length === 0) {
    return ''
  }
  if (data[0].id && data[0].userId && data[0].name && data[0].color) {
    return (
      <div>
        {data.map(tag => (
          <TagItem key={tag.id} entity={tag}></TagItem>
        ))}
      </div>
    )
  }
  if (data[0].userId || data[0].email) {
    return (
      <div>
        {data.map(contact => (
          <ContactItem key={contact.userId || contact.email} entity={contact}></ContactItem>
        ))}
      </div>
    )
  }

  return ''
}

const Container = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 8px;
  margin-right: 8px;
  margin-left: 9px;
  border-radius: 8px;
  &:hover {
    background-color: white;
  }
  background-color: ${props => (props.focus ? 'white' : '')};
  opacity: ${props => (props.hide ? 0 : 1)};
  transition: opacity 0.2s;
`
const ButtonContainer = styled.div`
  margin-right: 8px;
  margin-left: 26px;
  text-align: center;
  position: relative;
  ${media['phone-wide']`
    margin-left: 0px;
  `}
`

const PlusButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  cursor: pointer;
  background-color: ${styles.colors.buttonLightColor};
  width: 24px;
  height: 24px;
  line-height: 23px;
  padding-left: 2px;
  padding-right: 2px;
`

const TextareaContainer = styled(ReactTextareaAutocomplete)`
  background-color: initial;
  border: none;
  font: inherit !important;
  color: inherit;
  width: 100%;
  height: 23px;
  ::placeholder {
    font: inherit;
    color: inherit;
    opacity: 0.3;
  }
`

const TagContainer = styled.div`
  background-color: ${props => (props.selected ? selectedBackgroundColor : '')};
  padding: 5px;
`



// WEBPACK FOOTER //
// ./src/features/list/components/inlineAddTask/index.js